import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { lastValueFrom, map, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { MOCK_ROUTES } from 'src/app/components/reports/reports.constants';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import {
  IMAGE_SRC,
  STATUS_OPTIONS,
} from 'src/app/components/shared/shared-constants';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserAdmin } from 'src/app/core/models/admin';
import {
  DeliveryManifest,
  DeliveryManifestReport,
  DeliveryManifestReportRequest,
} from 'src/app/core/models/reports/reports.model';
import { OnRoadOperationsService } from 'src/app/core/services/reports/on-road-operations/on-road-operations.service';
import { PdfExportService } from 'src/app/core/services/reports/pdf-export.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { ReportsUtils } from 'src/app/core/utils/reports/reports.utils';
import { Options } from '../../../shared/report-format/report-format.constants';
import {
  CONSTS,
  DeliveryManifestFormGroup,
  DeliveryManifestGroup,
  PDF_REPORT,
  REPORT_FILE_NAME,
} from './delivery-manifest.constants';

@Component({
  selector: 'app-delivery-manifest',
  templateUrl: './delivery-manifest.component.html',
  styleUrls: ['./delivery-manifest.component.scss'],
  standalone: false,
})
export class DeliveryManifestComponent implements OnInit {
  readonly destroy = inject(DestroyRef);
  readonly input: DeliveryManifestFormGroup = inject(MAT_DIALOG_DATA);
  private readonly userService = inject(UserService);
  private readonly dialogRef: MatDialogRef<DeliveryManifestComponent> = inject(
    MatDialogRef<DeliveryManifestComponent>,
  );
  private readonly fb: FormBuilder = inject(FormBuilder);
  private readonly commonUtils: CommonUtils = inject(CommonUtils);
  private readonly reportUtils: ReportsUtils = inject(ReportsUtils);
  private readonly onRoadOperationService: OnRoadOperationsService = inject(
    OnRoadOperationsService,
  );
  private readonly pdfExportService: PdfExportService =
    inject(PdfExportService);

  readonly CONSTS = CONSTS;
  readonly IMAGES = IMAGE_SRC;
  readonly PDF_REPORT = PDF_REPORT;
  readonly BUTTON_TYPES = BUTTON_TYPES;
  driverUserID: string = '';

  deliveryManifestForm!: FormGroup;
  currDate!: Date;
  minDate!: Date;
  maxDate!: Date;
  routes = MOCK_ROUTES;
  currentUser!: UserAdmin;
  locationCode = '';
  deliveryManifestReport!: DeliveryManifestReport;
  deliveryManifestGroup: DeliveryManifestGroup[] = [];

  @ViewChild('pdfContainer', { read: ViewContainerRef })
  pdfContainer!: ViewContainerRef;
  reportLocation = '';
  isAllOptionSelected = true;

  /**
   * Lifecycle hook: Initializes component state once the component is created.
   *
   * Responsibilities:
   *  - Fetch the current user and derive the location code.
   *  - Trigger route list loading (dependent on location).
   *  - Compute date boundaries (min: 30 days before today, max: 14 days after today).
   *  - Initialize the delivery manifest form when no input is provided.
   *
   * Notes:
   *  - Uses `takeUntilDestroyed(this.destroy)` to auto-unsubscribe on component destroy.
   *  - Side effects (setting fields, invoking `getRouteList`) happen inside the pipe.
   *    Consider using `tap` for side effects if refactoring.
   */
  ngOnInit(): void {
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        map((user) => {
          this.currentUser = user;
          this.locationCode = this.currentUser?.location || '';
          this.getRouteList();

          this.currDate = new Date();
          this.minDate = new Date(
            this.currDate.getFullYear(),
            this.currDate.getMonth(),
            this.currDate.getDate() - 30,
          );
          this.maxDate = new Date(
            this.currDate.getFullYear(),
            this.currDate.getMonth(),
            this.currDate.getDate() + 14,
          );

          this.deliveryManifestForm = this.fb.group({
            fromDate: [this.currDate],
            toDate: [this.currDate],
            route: [this.routes[0]],
            reportFormat: [Options.PDF],
          });
        }),
      )
      .subscribe();
  }

  /**
   * Determines whether the current delivery manifest selection should be marked as duplicate
   * and sets the `driverUserID` based on the form's route selection.
   *
   * @param deliveryManifest - Array of DeliveryManifest objects.
   *   - Assumes the first element contains the relevant `userId` for comparison.
   *
   **/
  isDuplicate(deliveryManifest: DeliveryManifest[]) {
    const userId = deliveryManifest[0]?.userId;
    const formValue = this.deliveryManifestForm.value;
    this.driverUserID = formValue.route?.value === 'All' ? 'ALL' : userId;
  }

  /**
   * Sets the `reportLocation` property based on the delivery manifest data.
   *
   * @param deliveryManifestReport - The report object containing response details.
   * @param deliveryManifest - Array of DeliveryManifest entries used to determine unique locations.
   *
   * Logic:
   *  - Collect all `location` values from the deliveryManifest array.
   *  - Remove duplicates using a Set.
   *  - If multiple unique locations exist, use the location from the report response.
   *    Otherwise, use the single unique location from the manifest.
   */
  setReportLocation(
    deliveryManifestReport: DeliveryManifestReport,
    deliveryManifest: DeliveryManifest[],
  ) {
    let uniqueArray: string[] = [];
    const locations: string[] = [];
    deliveryManifest.forEach((report) => {
      locations.push(report.location);
    });
    uniqueArray = Array.from(new Set(locations));
    this.reportLocation =
      uniqueArray.length > 1
        ? deliveryManifestReport.response.location
        : uniqueArray[0];
  }

  /**
   * Handles the exit action for the Delivery Manifest component.
   *
   * Purpose:
   *  - Opens a confirmation modal when the user attempts to exit.
   *  - Passes the component reference, the current form instance, and its dirty state
   *    to determine if unsaved changes exist.
   *
   */
  onExit() {
    this.commonUtils.openExitModal(this.deliveryManifestForm.dirty);
  }

  /**
   * Handles the exit action for the Delivery Manifest component.
   **/
  radioChange(newValue: Options) {
    this.deliveryManifestForm.get('reportFormat')?.setValue(newValue);
  }

  /*
   *
   * Generates field index for the report
   *
   * */
  getFieldIndex(field: keyof DeliveryManifest, index: number, type = '/') {
    return field.split(type)[index] as keyof DeliveryManifest;
  }

  groupByStop(deliveryManifest: DeliveryManifest[]) {
    const completed = deliveryManifest.filter((val) => !!val.completed);
    const uncompleted = deliveryManifest.filter((val) => !val.completed);

    uncompleted.sort((a, b) => a.routeId.localeCompare(b.routeId));

    const groupedCompleted = this.groupVals(completed, false);
    groupedCompleted.sort((a, b) => a.stop - b.stop);

    const groupedUncompleted = this.groupVals(uncompleted, true);
    groupedUncompleted.sort((a, b) => a.stop - b.stop);

    return [...groupedCompleted, ...groupedUncompleted];
  }

  /**
   * Generates and exports the Delivery Manifest report based on the current form selections.
   *
   * Flow:
   *  1. Read form values and normalize the route selection (supports string or option object).
   *  2. Build the `DeliveryManifestReportRequest` payload (handles "All" selection).
   *  3. Show and lock the loading modal while the report is being fetched.
   *  4. Fetch the report (single emission) and determine output format (CSV or PDF).
   *  5. Handle edge cases:
   *     - No data → show "No Data Available" banner.
   *     - CSV → export with custom header.
   *     - PDF → group data, set report location, and export via PDF service.
   *  6. Update the banner based on success/failure and always release loading modal + close dialog.
   *
   *    */
  async getReport() {
    const formValue = this.deliveryManifestForm.value;
    const fromDate = this.commonUtils.getFormattedDate(formValue.fromDate);
    const toDate = this.commonUtils.getFormattedDate(formValue.toDate);

    const routeValue: string | undefined =
      typeof formValue.route === 'string'
        ? formValue.route
        : formValue.route?.value;

    this.isAllOptionSelected = routeValue === 'All';

    const request = {
      locationCode: this.locationCode,
      fromDate,
      toDate,
      loop: this.isAllOptionSelected ? 'All' : routeValue,
    } as DeliveryManifestReportRequest;

    this.commonUtils.updateLoadingModal(true);
    this.commonUtils.lockUpdateLoadingModal(true);

    try {
      const data = await lastValueFrom(
        this.onRoadOperationService
          .getDeliveryManifestReport(request)
          .pipe(take(1)),
      );

      const formatType = this.deliveryManifestForm.get('reportFormat')?.value;
      const deliveryManifest = data?.response?.deliveryManifest ?? [];

      this.isDuplicate(deliveryManifest);

      if (!deliveryManifest.length) {
        this.commonUtils.updateBannerType(BANNER_TYPE.NO_DATA_AVAILABLE);
        return;
      }

      if (formatType === Options.CSV) {
        const customHeader = [
          ['', '', '', '', '', '', '', '', 'DELIVERY MANIFEST REPORT'],
        ];
        this.reportUtils.exportCSVReport(
          deliveryManifest,
          REPORT_FILE_NAME,
          customHeader,
        );
        this.commonUtils.updateBannerType(BANNER_TYPE.DOWNLOAD_SUCCESSFUL);
        return;
      }

      if (formatType === Options.PDF) {
        this.deliveryManifestReport = data;
        this.deliveryManifestGroup = this.groupByStop(deliveryManifest);
        this.setReportLocation(data, deliveryManifest);

        const routeLabel = this.isAllOptionSelected
          ? 'ALL'
          : typeof formValue.route === 'string'
            ? formValue.route
            : formValue.route?.value;

        const userLabel = this.driverUserID ? String(this.driverUserID) : 'ALL';

        await this.pdfExportService.exportDeliveryManifestPdfAutoTable(
          this.deliveryManifestReport,
          this.deliveryManifestGroup,
          PDF_REPORT.NAME,
          REPORT_FILE_NAME,
          routeLabel,
          userLabel,
          this.commonUtils.getFormattedDate(formValue.fromDate),
          this.commonUtils.getFormattedDate(formValue.toDate),
          this.isAllOptionSelected,
          'p',
          'letter',
        );
      }
      this.commonUtils.updateBannerType(BANNER_TYPE.DOWNLOAD_SUCCESSFUL);
    } catch (err) {
      console.error('Delivery Manifest report generation failed:', err);
      this.commonUtils.updateBannerType(BANNER_TYPE.DOWNLOAD_UNSUCCESSFUL);
    } finally {
      this.commonUtils.lockUpdateLoadingModal(false);
      this.commonUtils.updateLoadingModal(false);
      this.dialogRef.close();
    }
  }

  /**
   * Loads and prepares the list of delivery manifest routes for the current `locationCode`.
   *
   * Flow:
   *  - Calls `getDeliveryManifestRoutes(locationCode)` to fetch routes.
   *  - Maps raw route strings into `{ label, value }` option objects.
   *  - Sorts routes alphabetically by label.
   *  - Prepends an "All" option for selecting all routes.
   *
   * Notes:
   *  - Uses `take(1)` to complete after the first emission.
   *  - Mutates `this.routes` with the prepared options for binding in the UI.
   */
  private getRouteList() {
    this.onRoadOperationService
      .getDeliveryManifestRoutes(this.locationCode)
      .pipe(
        take(1),
        map((data) => {
          this.routes = data.response.deliveryManifestRoutes
            .map(({ route }) => {
              return {
                label: route,
                value: route,
              };
            })
            .sort((a, b) => a.label.localeCompare(b.label));
          this.routes.unshift({
            label: STATUS_OPTIONS.ALL_SELECTED,
            value: 'All',
          });
        }),
      )
      .subscribe();
  }

  private groupVals(
    inputVals: DeliveryManifest[],
    completed: boolean,
  ): DeliveryManifestGroup[] {
    const outputVal: DeliveryManifestGroup[] = [];
    inputVals.forEach((completedVal) => {
      const index = outputVal.findIndex(
        (value: DeliveryManifestGroup) => value.stop === completedVal.stop,
      );
      if (index > -1) {
        outputVal[index].deliveryManifest.push(completedVal);
      } else {
        outputVal.push({
          stop: completedVal.stop,
          deliveryManifest: [completedVal],
          needsCompletion: completed,
        });
      }
    });

    return outputVal.map((val) => {
      const newManifest = val.deliveryManifest.map((item, i) => {
        if (i == 0) {
          return item;
        } else {
          return {
            ...item,
            routeId: '',
            customer: '',
            streetAddress: '',
            city: '',
            prov: '',
            postal: '',
          };
        }
      });
      return {
        stop: val.stop,
        deliveryManifest: newManifest,
        needsCompletion: val.needsCompletion,
      };
    });
  }
}
